/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import * as React from 'react';
import Emoji from 'react-emoji-render';
import { Popover } from 'react-tiny-popover';
import { AnimatePresence } from 'framer-motion';
import styled, { css } from 'styled-components';

import { CommentIcon, PlusIcon } from '../../icons/Icons';
import { ANIMATIONS } from '../../utils';

import { Avatar } from './Avatar';
import { type PopoverArrowPosition, PopoverContainer } from './Popover';
import { MinimalButton, OutlinedButton } from './SharedStyles';

const StyledPopoverContainer = styled(PopoverContainer)`
  max-width: 320px;
  min-width: 250px;
`;

const Comments = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
`;

const AddComment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-top: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
  padding: 5px;
`;

const AddCommentButton = styled(MinimalButton)`
  flex: 1 1 auto;
`;

const Comment = styled.div<{ $isReply?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ $isReply }) =>
    $isReply &&
    css`
      padding-left: 20px;
      border-left: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
    `}
`;

const CommentTop = styled.div`
  display: flex;
  align-items: center;
`;

const AvatarWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.LIGHT_PURPLE};
  border-radius: 50%;
  overflow: hidden;
  margin-right: 8px;

  > div {
    width: 100%;
    height: 100%;
  }
`;

const Name = styled.div`
  font-weight: 500;
`;

const Time = styled.div`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.appUI.fontColor.MUTED};
  margin-left: auto;
`;

const CommentBody = styled.p``;

const Emojis = styled.div`
  display: flex;
  gap: 3px;

  > div {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0 6px 0 3px;
    border-radius: ${({ theme }) => theme.borderRadius};
    color: ${({ theme }) => theme.appUI.fontColor.MUTED};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    border: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
  }
`;

interface CommentType {
  content: string;
  emojis?: ReactNode[];
  image: ReactNode;
  isReply?: boolean;
  name: string;
  time: string;
}

const COMMENTS: CommentType[] = [
  {
    image: <Avatar slug="lady" />,
    name: 'Jessica',
    time: '12 min ago',
    content:
      'We should refactor this to use the new rollup table to improve performance.',
    emojis: [<Emoji text=":plus_sign:" />, <Emoji text=":100:" />]
  },
  {
    image: <Avatar slug="duncan" />,
    name: 'Duncan',
    time: '11 min ago',
    isReply: true,
    content: "Great idea. I'll handle this on the next pass.",
    emojis: [<Emoji text=":thumbs_up:" />]
  }
];

interface CommentButtonProps {
  disabled?: boolean;
  popupOverride?: boolean;
  showCommentCount?: boolean;
}

export const CommentButton: React.ComponentType<CommentButtonProps> =
  React.memo(
    ({ showCommentCount = false, disabled = false, popupOverride }) => {
      const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

      useEffect(() => {
        if (popupOverride !== undefined) {
          setIsPopoverOpen(popupOverride);
        }
      }, [popupOverride]);

      const _togglePopover = useCallback(() => {
        if (popupOverride === undefined && !disabled) {
          setIsPopoverOpen(prev => !prev);
        }
      }, [disabled, popupOverride]);

      return (
        <Popover
          align="center"
          containerStyle={{ zIndex: '99999' }}
          content={<CommentButtonPopover arrowPosition="right-center" />}
          isOpen={isPopoverOpen}
          padding={10}
          positions={['left']}
          onClickOutside={_togglePopover}>
          <OutlinedButton
            $active={isPopoverOpen}
            $disabled={!isPopoverOpen}
            $minimal={true}
            $small={true}
            onClick={_togglePopover}>
            <CommentIcon /> {showCommentCount ? COMMENTS.length : null}
          </OutlinedButton>
        </Popover>
      );
    }
  );

interface CommentButtonPopoverProps {
  arrowPosition: PopoverArrowPosition;
}

export const CommentButtonPopover: React.ComponentType<CommentButtonPopoverProps> =
  React.memo(({ arrowPosition }) => {
    return (
      <AnimatePresence>
        <StyledPopoverContainer
          $position={arrowPosition}
          {...ANIMATIONS.Popover}>
          <Comments>
            {COMMENTS.map((c, i) => (
              <Comment key={i} $isReply={c.isReply}>
                <CommentTop>
                  <AvatarWrapper>{c.image}</AvatarWrapper>
                  <Name>{c.name}</Name>
                  <Time>{c.time}</Time>
                </CommentTop>
                <CommentBody>{c.content}</CommentBody>
                {c.emojis && c.emojis.length > 0 && (
                  <Emojis>
                    {c.emojis.map((e, ix) => (
                      <div key={ix}>{e} 1</div>
                    ))}
                  </Emojis>
                )}
              </Comment>
            ))}
          </Comments>
          <AddComment>
            <AddCommentButton>
              <PlusIcon />
              Add comment
            </AddCommentButton>
          </AddComment>
        </StyledPopoverContainer>
      </AnimatePresence>
    );
  });
